/**
 * Override del UserMenu de TZU.
 *
 * Añade en el desplegable:
 *  - Nombre de la organización activa (obtenido de GET /api/v1/orgs/{org_id})
 *  - Créditos restantes (obtenido de GET /api/v1/orgs/{org_id}/credits)
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Text,
  Box,
  HStack,
  Badge,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { FaUser, FaSignOutAlt, FaChevronDown, FaBuilding, FaCoins } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import {
  userMenuButtonStyle,
  userMenuItemStyle,
  logoutMenuItemStyle,
} from "../styles/HeaderStyles";

function decodeJwtPayload(token) {
  try {
    const part = token?.split(".")?.[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function getOrgIdFromToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload) return null;
    return payload.org_id ?? null;
  } catch {
    return null;
  }
}

function useOrgInfo() {
  const [orgName, setOrgName] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrgInfo = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setOrgName(null);
      setCredits(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    let orgId = getOrgIdFromToken();

    // Fallback for legacy/mismatched tokens that don't include org_id.
    if (!orgId) {
      try {
        const orgsResp = await fetch("/api/v1/orgs/", { headers });
        if (orgsResp.ok) {
          const orgs = await orgsResp.json();
          if (Array.isArray(orgs) && orgs.length > 0) {
            orgId = orgs[0].org_id;
            if (orgs[0]?.name) setOrgName(orgs[0].name);
          }
        }
      } catch {
        // Keep null state and let UI render dashes.
      }
    }

    if (!orgId) {
      setOrgName(null);
      setCredits(null);
      setLoading(false);
      return;
    }

    Promise.all([
      fetch(`/api/v1/orgs/${orgId}`, { headers }).then((r) => (r.ok ? r.json() : null)),
      fetch(`/api/v1/orgs/${orgId}/credits`, { headers }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([orgData, creditData]) => {
        if (orgData?.name) {
          setOrgName(orgData.name);
        }
        if (creditData?.credit_balance !== undefined) setCredits(creditData.credit_balance);
      })
      .catch(() => {
        setOrgName(null);
        setCredits(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOrgInfo();

    const handleRefresh = () => fetchOrgInfo();
    window.addEventListener("org-credits-refresh", handleRefresh);
    return () => window.removeEventListener("org-credits-refresh", handleRefresh);
  }, [fetchOrgInfo]);

  return { orgName, credits, loading, refreshOrgInfo: fetchOrgInfo };
}

const UserMenu = () => {
  const { user, logout } = useAuth();
  const toast = useToast();
  const { orgName, credits, loading, refreshOrgInfo } = useOrgInfo();
  const name = user?.name || "Usuario";

  const handleLogout = () => {
    logout();
    localStorage.removeItem("token");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("pendingInviteToken");
    toast({
      title: "Sesión cerrada",
      description: "Has cerrado sesión correctamente",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    // Full navigation avoids stale in-memory auth state in long-lived SPA sessions.
    window.location.assign("/login");
  };

  const isLowCredits = credits !== null && credits <= 5;

  return (
    <Menu onOpen={refreshOrgInfo}>
      <MenuButton
        as={Button}
        rightIcon={<FaChevronDown />}
        leftIcon={<FaUser />}
        {...userMenuButtonStyle}
      >
        {name}
      </MenuButton>

      <MenuList bg="white" borderColor="gray.200" minW="220px">
        {/* Info del usuario */}
        <MenuItem icon={<FaUser />} {...userMenuItemStyle} isDisabled>
          <Box>
            <Text fontSize="sm" fontWeight="bold">{name}</Text>
            <Text fontSize="xs" color="gray.500">
              {user?.role ?? "miembro"}
            </Text>
          </Box>
        </MenuItem>

        <MenuDivider />

        {/* Organización y créditos */}
        {loading ? (
          <MenuItem isDisabled>
            <HStack spacing={2}>
              <Spinner size="xs" color="orange.400" />
              <Text fontSize="xs" color="gray.400">Cargando...</Text>
            </HStack>
          </MenuItem>
        ) : (
          <>
            {/* Organización */}
            <MenuItem icon={<FaBuilding />} {...userMenuItemStyle} isDisabled>
              <Box>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">
                  Organización
                </Text>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  {orgName ?? "—"}
                </Text>
              </Box>
            </MenuItem>

            {/* Créditos */}
            <MenuItem icon={<FaCoins />} {...userMenuItemStyle} isDisabled>
              <Box>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">
                  Créditos disponibles
                </Text>
                <HStack spacing={2} mt="1px">
                  <Text fontSize="sm" fontWeight="bold" color={isLowCredits ? "red.500" : "orange.500"}>
                    {credits !== null ? credits : "—"}
                  </Text>
                  {isLowCredits && (
                    <Badge colorScheme="red" fontSize="0.65em" variant="subtle">
                      bajo
                    </Badge>
                  )}
                </HStack>
              </Box>
            </MenuItem>
          </>
        )}

        <MenuDivider />

        {/* Cerrar sesión */}
        <MenuItem
          icon={<FaSignOutAlt />}
          {...logoutMenuItemStyle}
          onClick={handleLogout}
        >
          Cerrar Sesión
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default UserMenu;
